import {
  ReactNode,
  createContext,
  FC,
  useEffect,
  useState,
  useRef,
  useMemo
} from 'react'
import {
  getQuestions,
  updateQuestions,
  submitAnswer,
  submitAssessment,
  toggleBookmark
} from 'services/AssessmentService'
import {
  selectAssessments,
  setSelectedAssessment,
  putTestTimeAction
} from 'app/reducer/assessmentSlice'
import { useAppDispatch } from 'app/hooks'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import Cookies from 'js-cookie'
import socketKeys from './socket-keys'
import { io } from 'socket.io-client'
import {
  eraseDrawing,
  getMarkings,
  getMarkingsQuestions
} from 'util/RaphaelInit'
import { ApiFailure } from 'index'
import {
  isMultipleLoginErrorSelector,
  setMultipleLoginError
} from 'app/reducer/errorSlice'
import { selectUser } from 'app/reducer/authSlice'
import { isNumber } from 'mathjs'
import { beforeUnloadHandler, socketSetter } from 'util/beforeUnloadHandler'
import { getCalculator, setCalculator } from './Footer'
import { getSingularAssessmentType } from 'util/assessments'
import { AssessmentQuestion } from 'interfaces/assessments'
import { ViewMode } from 'interfaces/app'

interface QuestionnaireContextModal {
  audioRef: any
  getCalculatorElement: () => any
  closeCalculator: () => void
  destroyCalculator: () => void
  setIsSpeechIconClicked: (a: boolean) => void
  isSpeechIconClicked: boolean
  isHighlighterActive: boolean
  setIsHighlighterActive: (isHighlighterActive: boolean) => void
  setShowCalculator: (show: boolean) => void
  showCalculator: boolean
  hasPassage: boolean
  showTabs: boolean
  viewMode: ViewMode
  setSearchParams: (key: any) => void
  questionIdx: number
  setQuestionIdx: (idx: number) => void
  questions: AssessmentQuestion[]
  currentQuestion: AssessmentQuestion
  nextQuestion: () => void
  previousQuestion: () => void
  hasNextQuestion: boolean
  hasPreviousQuestion: boolean
  isTestUpdated: boolean
  onRevert: () => void
  onSave: () => void
  onClear: () => void
  onExit: () => void
  onChange: (key: string, value: any) => void
  show: boolean
  socketRef: any
  setShow: (value: boolean) => void
  tabIndex: number
  setTabIndex: (tabIndex: number) => void
  showSaveModal: boolean
  setShowSaveModal: (showModal: boolean) => void
  showClearModal: boolean
  setShowPauseExitModal: (showModal: boolean) => void
  setShowClearModal: (showModal: boolean) => void
  showPauseExitModal: boolean
  showFinishModal: boolean
  setShowFinishModal: (show: boolean) => void
  isSocketReady: boolean
  keyToSpeak: string
  setKeyToSpeak: (key: string) => void
  keys: string[]
  onSpeak: (key: string) => void
  isPencilActive: boolean
  setIsPencilActive: (pecnilActive: boolean) => void
  setIsPaused: (pause: boolean) => void
  isPaused: boolean
  onSubmitAssessment: () => void
  setIsScreenMarked: (a: boolean) => void
  onAnswerChange: (choice: number) => void
  isPausedByTeacher: boolean
  setIsPausedByTeacher: (a: boolean) => void
  onChangeQuestionFromTopBar: (questionNumber: number) => void
  showUnAvailableTestModal: boolean
  setShowUnAvailableTestModal: (show: boolean) => void
  assessmentScore: number
  setAssessmentScore: (score: number) => void
  saveExpressionAndMarkings: () => void
  containerRef: any
  submitTestLoading: boolean
  grade: number
  setSubmitTestLoading: (a: boolean) => void
  setShowHighlightEraser: (cond: boolean) => void
  showHighlightEraser: boolean
  setIsExpand: (expand: boolean) => void
  isExpand: boolean
  answerPromises: Promise<any>[]
  setAnswerPromises: (promises: Promise<any>[]) => void
  answerSubmittingModal: boolean
  setAnswerSubmittingModal: (set: boolean) => void
  sketchWidth: any
  setSketchWidth: (widht: any) => void
  setSketchLeft: (left: any) => void
  sketchLeft: any
  scrolledTop: any
  setScrolledTop: (scrolled: any) => void
  setSketchHeight: (height: any) => void
  sketchHeight: any
  setSketchHeightQues: (height: any) => void
  setSketchWidthQues: (width: any) => void
  sketchHeightQues: any
  sketchWidthQues: any
}

const questionKeys = [
  'passage_directions',
  'direction',
  'question',
  'choice_1',
  'choice_2',
  'choice_3',
  'choice_4'
]

export const QuestionnaireContext = createContext(
  {} as QuestionnaireContextModal
)

interface Props {
  children: ReactNode | ReactNode[]
}

const QuestionnaireContextProvider: FC<Props> = (props) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { assessmentType, id } = useParams()
  const isFormative = assessmentType === 'formatives'
  const isBenchmark =
    assessmentType === 'benchmarks' || assessmentType === 'summatives'
  const isAssignment = assessmentType === 'assignments'

  const assessments = useSelector(selectAssessments)
  const authToken = useSelector(selectUser)?.token

  const classroom_id = Cookies.get('classroom_id')
  const student_id = Cookies.get('student_id') || ''
  const studentId = Number(Cookies.get('id_student'))

  const [searchParams, setSearchParams] = useSearchParams()

  const viewMode: ViewMode = useMemo(() => {
    return Number(searchParams.get('viewMode'))
  }, [searchParams])
  /**
   * The benchmark review launch reuses the existing student view mode flag.
   * Normal student test-taking does not include the query param, so read only
   * behavior should only turn on when that explicit mode is present in the URL.
   */
  const isReadOnlyViewMode =
    searchParams.get('viewMode') === String(ViewMode.Student) &&
    viewMode === ViewMode.Student

  const [answerSubmittingModal, setAnswerSubmittingModal] = useState(false)
  const [answerPromises, setAnswerPromises] = useState<Promise<any>[]>([])
  const [showHighlightEraser, setShowHighlightEraser] = useState(false)
  const [submitTestLoading, setSubmitTestLoading] = useState(false)
  const [questions, setQuestions] = useState<AssessmentQuestion[]>([])
  const [questionIdx, setQuestionIdx] = useState<number>(0)
  const [show, setShow] = useState(false)
  const [tabIndex, setTabIndex] = useState(0)
  const [grade, setGrade] = useState(3)
  const [showSaveModal, setShowSaveModal] = useState(false)
  const [showClearModal, setShowClearModal] = useState(false)
  const [showPauseExitModal, setShowPauseExitModal] = useState(false)
  const [isExpand, setIsExpand] = useState(false)
  const [showFinishModal, setShowFinishModal] = useState(false)
  const [showCalculator, setShowCalculator] = useState(false)
  const [isHighlighterActive, setIsHighlighterActive] = useState(false)
  const [isSpeechIconClicked, setIsSpeechIconClicked] = useState(false)
  const [keyToSpeak, setKeyToSpeak] = useState<string>(null!)
  const [isPencilActive, setIsPencilActive] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [initUpdatePostion, setInitUpdatePostion] = useState(false)
  const [isPausedByTeacher, setIsPausedByTeacher] = useState(false)
  const [socketInit, setSocketInit] = useState(false)
  const [isSocketReady, setIsSocketReady] = useState(false)
  const [showUnAvailableTestModal, setShowUnAvailableTestModal] =
    useState(false)
  const [assessmentScore, setAssessmentScore] = useState<number>(null!)
  const [keys, setKeys] = useState<string[]>([...questionKeys])
  const currentQuestion: any = questions[questionIdx] || {}
  const socketRef = useRef<any>(null)
  const hasSocketConnectionErrorRef = useRef(false)
  const isMultipleLoginError = useSelector(isMultipleLoginErrorSelector)
  const containerRef = useRef<HTMLDivElement>(null)
  const audioRef = useRef<any>(null)
  const [sketchWidth, setSketchWidth] = useState(undefined)
  const [sketchLeft, setSketchLeft] = useState(undefined)
  const [scrolledTop, setScrolledTop] = useState(undefined)
  const [sketchHeight, setSketchHeight] = useState(undefined)
  const [sketchHeightQues, setSketchHeightQues] = useState(undefined)
  const [sketchWidthQues, setSketchWidthQues] = useState(undefined)

  const {
    SOCKET_STUDENT_UPDATE_ANSWER,
    SOCKET_STUDENT_SUBMIT,
    SOCKET_STUDENT_JOIN_ROOM,
    SOCKET_STUDENT_UPDATE_POSITION_QUESTION,
    SOCKET_TEACHER_PAUSE_RESUME,
    SOCKET_TEACHER_FORCE_SUBMIT,
    SOCKET_TEACHER_KICK_STUDENT,
    SOCKET_TEACHER_CLEAR_ANSWER,
    SOCKET_CLASS_BY_ID_CHANNEL,
    SOCKET_STUDENT_TEST_TIME,
    SOCKET_STUDENT_LEAVE_ROOM,
    SOCKET_DISCONNECT_EVENT,
    SOCKET_STUDENT_LOG_OUT,
    SOCKET_TEACHER_VIEW_UPDATE
  } = socketKeys

  const sharePassageWith = (
    currentQuestion: AssessmentQuestion,
    questions: AssessmentQuestion[]
  ) => {
    if (currentQuestion.shares_passage_with) {
      const sharedQuestion = questions.find(
        (qes: any) => qes.id === currentQuestion.shares_passage_with
      )

      if (sharedQuestion) {
        currentQuestion['passage_directions'] =
          sharedQuestion.passage_directions
        currentQuestion['passage_1'] = sharedQuestion.passage_1
        currentQuestion['passage_2'] = sharedQuestion.passage_2
        currentQuestion['hasPassage1'] = !!sharedQuestion.passage_1
        currentQuestion['hasPassage2'] = !!sharedQuestion.passage_2
      }
    }
  }

  const asyncFetchQuestions = async (
    assessmentType: string,
    assessmentId: string,
    classroomId: string
  ) => {
    try {
      const data = await getQuestions(
        assessmentType,
        parseInt(assessmentId),
        parseInt(classroomId)
      )

      Cookies.set('assessmentName', data.title)
      Cookies.set('allow_calculator', `${data.allow_calculator}`)
      Cookies.set('allow_ti84_calculator', `${data.allow_ti84_calculator}`)
      /**
       * Assessment tool permissions are persisted in cookies so the assessment
       * shell and shared student tools can read the same backend flags without
       * threading extra props through each assessment layout layer.
       */
      Cookies.set('other_tools', `${data.other_tools}`)
      Cookies.set('text_to_speech_enabled', `${data.text_to_speech_enabled}`)
      if (data) {
        if (data.grade === 'Kindergarten') {
          setGrade(9)
        } else {
          data.grade && setGrade(+data.grade)
        }
        setIsPausedByTeacher(data.is_paused)
      }

      setQuestions(
        data.questions.map((q: any, index: number) => {
          const parsedStudentAnswer = parseInt(q.student_answer)
          const parsedCorrectAnswer = parseInt(q.answer)

          /**
           * Standard student mode keeps `answer` mapped to the submitted
           * response because the active benchmark shell already reads that key.
           * Read only mode keeps the correct answer in `answer` and exposes the
           * submitted response separately for correct and incorrect UI states.
           */
          const currentQuestion = {
            ...q,
            questionNo: index + 1,
            answer: isReadOnlyViewMode
              ? isNaN(parsedCorrectAnswer)
                ? null
                : parsedCorrectAnswer
              : isNaN(parsedStudentAnswer)
                ? null
                : parsedStudentAnswer,
            student_answer: isNaN(parsedStudentAnswer)
              ? null
              : parsedStudentAnswer,
            penMarkings: [],
            isScreenMarked: false
          }

          sharePassageWith(currentQuestion, data.questions)

          return currentQuestion
        })
      )
    } catch (error) {
      console.error('error', error)
    }
  }

  useEffect(() => {
    return () => {
      eraseDrawing()
    }
  }, [])

  useEffect(() => {
    let element: HTMLElement | null = null

    const isValidKeyToSpeak =
      keyToSpeak &&
      ['passage_1', 'passage_2'].some((passage) => keyToSpeak.includes(passage))

    if (isValidKeyToSpeak) {
      element = document.getElementById(keyToSpeak)
      if (element) {
        element.classList.add('playtts')
      }
    }
    if (keyToSpeak === 'passage_directions' || isValidKeyToSpeak) {
      element = document.getElementById(keyToSpeak)
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    return () => {
      if (element) {
        element.classList.remove('playtts')
      }
    }
  }, [keyToSpeak])

  useEffect(() => {
    const token = Cookies.get('token')

    /**
     * React Router keeps this provider mounted across in-app navigation
     * (login -> classroom included), so this effect must re-run when the
     * authenticated user changes rather than only once on first mount.
     * Depending solely on `[]` meant a provider that first mounted on the
     * pre-login route would never open a socket until a hard refresh, since
     * the token cookie didn't exist yet at that first run.
     */
    if (!token) return

    socketRef.current = io(process.env.REACT_APP_API_URL as string, {
      transports: ['websocket'],
      auth: { token },
      query: {}
    })

    socketSetter(socketRef.current)

    socketRef.current.io.engine.opts.query.reconnecting = true

    const unloadHandler = (event: Event) => {
      event.preventDefault()
      socketRef.current?.close()
    }

    window.addEventListener('beforeunload', beforeUnloadHandler)
    window.addEventListener('unload', unloadHandler)

    socketRef.current?.on('connect_error', function (error: any) {
      hasSocketConnectionErrorRef.current = true
      ApiFailure.setApiError(true, null!)
    })

    socketRef.current?.on('connect', () => {
      /**
       * A successful connection after a socket failure means connectivity was
       * restored. Reload so assessment data and socket subscriptions restart
       * from the server's current state, matching the teacher portal recovery.
       */
      if (hasSocketConnectionErrorRef.current) {
        window.location.reload()
        return
      }

      setIsSocketReady(true)

      socketRef.current?.on(SOCKET_STUDENT_LOG_OUT, async (data: any) => {
        dispatch(setMultipleLoginError(true))
      })

      socketRef.current?.on(SOCKET_CLASS_BY_ID_CHANNEL, async (data: any) => {
        ApiFailure.setApiError(false, null!)
      })
    })

    socketRef.current?.on('disconnect', () => {
      setIsSocketReady(false)
    })

    return () => {
      /**
       * Runs on token change (e.g. login/logout) as well as unmount, closing
       * the previous connection before a new one opens for the new user.
       */
      window.removeEventListener('beforeunload', beforeUnloadHandler)
      window.removeEventListener('unload', unloadHandler)
      hasSocketConnectionErrorRef.current = false
      setIsSocketReady(false)
      socketRef.current?.close()
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [authToken])

  useEffect(() => {
    const socket = socketRef.current

    /**
     * The context-owned connection joins the currently opened assessment room
     * on initial connect and reconnect.
     */
    if (!socket || !assessmentType || !id || !classroom_id || !student_id) {
      return
    }

    const emitAssessmentJoinRoom = () => {
      socket.emit(SOCKET_STUDENT_JOIN_ROOM, {
        classroom_id,
        student_id,
        test_type: getSingularAssessmentType(assessmentType),
        test_id: id
      })
    }

    if (socket.connected) {
      emitAssessmentJoinRoom()
    }

    socket.on('connect', emitAssessmentJoinRoom)

    return () => {
      socket.off('connect', emitAssessmentJoinRoom)
    }
  }, [assessmentType, id])

  useEffect(() => {
    let interval: any = null
    /**
     * Time tracking belongs only to an opened assessment route even though
     * the context and its socket now exist on every application route.
     */
    if (
      assessmentType &&
      id &&
      !isPaused &&
      !isPausedByTeacher &&
      !isMultipleLoginError
    ) {
      interval = setInterval(() => {
        id &&
          dispatch(
            putTestTimeAction(
              parseInt(id),
              getSingularAssessmentType(assessmentType) as string,
              classroom_id
            )
          )

        socketRef.current?.emit(SOCKET_STUDENT_TEST_TIME, {
          classroom_id,
          student_id,
          test_type: getSingularAssessmentType(assessmentType),
          test_id: id
        })
      }, 1000 * 60)
    }

    return () => {
      interval && clearInterval(interval)
    }
  }, [isPaused, isPausedByTeacher, isMultipleLoginError, assessmentType, id])

  useEffect(() => {
    const socket = socketRef.current

    /**
     * Classroom and authentication routes use their own socket event handlers;
     * assessment-specific teacher events require assessment route identifiers.
     */
    if (!socket || !assessmentType || !id) return

    const handleAssessmentClassEvent = async (data: any) => {
      const { type, classroom_id: classroomId, test_id } = data.resource
      const event = data.resource.event || ''
      const isPauseSessionEvent = event === 'pause_session'
      if (type === SOCKET_STUDENT_JOIN_ROOM) {
        setSocketInit(true)
      } else if (type === SOCKET_TEACHER_PAUSE_RESUME) {
        if (
          Number(classroomId) === Number(classroom_id) &&
          Number(test_id) === Number(id)
        ) {
          setIsPausedByTeacher((prevState) => !prevState)
        }
      } else if (
        type === SOCKET_TEACHER_VIEW_UPDATE &&
        isPauseSessionEvent &&
        Number(classroomId) === Number(classroom_id) &&
        Number(test_id) === Number(id)
      ) {
        debugger
        setIsPausedByTeacher(Boolean(data.resource.isPaused))
      } else if (type === SOCKET_TEACHER_FORCE_SUBMIT) {
        if (
          data.resource.student_id === null ||
          data.resource.student_id === undefined ||
          data.resource.student_id === studentId
        ) {
          onSubmitAssessment()
          // navigation added here because of additional modal on submit for score
          navigate('/classroom')
        }
      } else if (type === SOCKET_TEACHER_KICK_STUDENT) {
        if (
          !isNumber(data.resource.student_id) ||
          data.resource.student_id === studentId
        )
          setShowUnAvailableTestModal(true)
      } else if (
        type === SOCKET_TEACHER_CLEAR_ANSWER ||
        (type === SOCKET_TEACHER_VIEW_UPDATE && event === 'clear_answers')
      ) {
        if (
          data.resource.student_id === null ||
          data.resource.student_id === undefined
        ) {
          onClearAnswers()
        } else if (data.resource.student_id === studentId) {
          onClearAnswers()
        }
      }
    }

    socket.on(SOCKET_CLASS_BY_ID_CHANNEL, handleAssessmentClassEvent)

    return () => {
      /**
       * Remove only this context listener because dashboard and assignment
       * routes can share the same channel on the shared socket instance.
       */
      socket.off(SOCKET_CLASS_BY_ID_CHANNEL, handleAssessmentClassEvent)
    }
  }, [questions, assessmentType, id])

  useEffect(() => {
    /**
     * Mounting the provider around non-assessment routes must not trigger the
     * assessment fallback redirect or request assessment questions.
     */
    if (!assessmentType || !id) return

    /**
     * Each assessment route must start from its first question instead of
     * retaining the question position from the previously opened assessment.
     */
    setQuestionIdx(0)

    if ((isFormative || isBenchmark) && id && classroom_id) {
      asyncFetchQuestions(assessmentType, id, classroom_id)
    } else if (isAssignment) {
      // keeping this block to prevent redirects
    } else {
      navigate('/classroom')
    }
  }, [assessmentType, id, navigate])

  useEffect(() => {
    if (assessments.length > 0 && id && (isFormative || isBenchmark)) {
      const selectedAssessment = assessments.find(
        (assessment: any) =>
          assessment.id === parseInt(id) && assessment.type === assessmentType
      )
      if (selectedAssessment) {
        dispatch(setSelectedAssessment(selectedAssessment.name))
      }
    }
  }, [dispatch, assessments, id, assessmentType])

  useEffect(() => {
    const isAssessmentV1 = localStorage.getItem('assessment') === 'V1'

    if (isAssessmentV1) {
      const passageId = `passage_${tabIndex + 1}`

      const passageNodes = document
        .getElementById(passageId)
        ?.querySelectorAll('p, li, span, td')

      const passageKeys: string[] = []
      passageNodes?.forEach((node: any, idx) => {
        const hasAncestorInList = (node: any) => {
          let current = node.parentElement
          while (current) {
            if (Array.from(passageNodes).includes(current)) {
              return true
            }
            current = current.parentElement
          }
          return false
        }

        if (hasAncestorInList(node)) {
          return
        }

        if (node?.innerText?.trim().length) {
          const passageKey = `${passageId}_${idx + 1}`
          node.id = passageKey
          currentQuestion[passageKey] = node.innerText
          passageKeys.push(passageKey)
          node.onclick = () => isSpeechIconClicked && setKeyToSpeak(passageKey)

          // node?.classList?.add('my-4')
        } else {
          // There is one scenario where the html tag has only one tag and that is <img> tag.
          // in that case, the innerText in above IF condition would evaluate to FALSE
          // but we don't want to delete this NODE because the text in this NODE is empty
          // but it still has <img> tag.
          if (!node?.innerHTML?.trim().includes('img')) {
            node?.classList?.add('no-dotted')
          }
        }
      })

      const newKeys = [...questionKeys]

      newKeys.splice(1, 0, ...passageKeys)

      setKeys(newKeys)

      return () => {
        const passageNodes = document
          .getElementById(passageId)
          ?.querySelectorAll('p')

        passageNodes?.forEach((node: any) => {
          node.onclick = () => false
        })
        setKeys([...questionKeys])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion.id, isSpeechIconClicked, tabIndex])

  const DO_NOT_HARVEST = new Set([
    'choice_1',
    'choice_2',
    'choice_3',
    'choice_4'
  ])

  const getDomValues = () => {
    const domValues: any = {}
    const root = containerRef.current as HTMLElement | null

    const allKeys = [...keys, 'passage_1', 'passage_2'].filter(
      (k) => !DO_NOT_HARVEST.has(k)
    )

    allKeys.forEach((k) => {
      const el = root?.querySelector<HTMLElement>('#' + k) // ✅ container-scoped
      if (el?.innerHTML) {
        /**
         * Highlighter wrappers are temporary assessment-session UI. Unwrap
         * them from a clone so navigation never copies markings into question
         * content or any later assessment payload.
         */
        const contentClone = el.cloneNode(true) as HTMLElement
        contentClone
          .querySelectorAll<HTMLElement>('[data-highlighted="true"]')
          .forEach((highlight) => {
            highlight.replaceWith(...Array.from(highlight.childNodes))
          })
        domValues[k] = contentClone.innerHTML
      }
    })

    return domValues
  }

  const onSubmitAssessment = async () => {
    if ((isFormative || isBenchmark) && id && classroom_id) {
      setSubmitTestLoading(true)
      const studentAnswers = questions.reduce((acc: any, item, idx: number) => {
        const key = `question_${idx + 1}_response`
        acc[key] = item.answer
        return acc
      }, {})
      const resp = await submitAssessment(
        assessmentType,
        parseInt(id),
        parseInt(classroom_id),
        studentAnswers
      )

      setSubmitTestLoading(false)
      if (resp?.submitted) {
        socketRef.current?.emit(SOCKET_STUDENT_SUBMIT, {
          classroom_id: classroom_id,
          test_type: getSingularAssessmentType(assessmentType),
          test_id: id,
          student_id
        })
        setAnswerPromises([])
      }

      setAssessmentScore(resp.score_percentage)
    }
    setShowFinishModal(false)
  }

  const onClearAnswers = () => {
    const updatedQuestions: AssessmentQuestion[] = questions.map(
      (question) => ({
        ...question,
        answer: null!
      })
    )

    setQuestions(updatedQuestions)
  }

  const onSpeak = (key: any) => {
    isSpeechIconClicked && setKeyToSpeak(key)
  }

  const saveExpressionAndMarkings = () => {
    const penMarkings = getMarkings()
    const penMarkingsQuestions = getMarkingsQuestions()
    const domValues = getDomValues()

    const modifiedQuestions = questions.map((item, index: number) => {
      if (index === questionIdx) {
        return {
          ...item,
          penMarkings: penMarkings,
          penMarkingsQuestions: penMarkingsQuestions,
          ...domValues
        }
      }

      return item
    })

    const currQuestion = modifiedQuestions[questionIdx]
    const updatedQuestions = modifiedQuestions.map((q: AssessmentQuestion) => {
      if (
        q.shares_passage_with === currQuestion.id ||
        currQuestion.shares_passage_with === q.id ||
        (currQuestion.shares_passage_with &&
          q.shares_passage_with &&
          currQuestion.shares_passage_with === q.shares_passage_with)
      ) {
        q['passage_directions'] = currQuestion.passage_directions
        q['passage_1'] = currQuestion.passage_1
        q['passage_2'] = currQuestion.passage_2
        q['isScreenMarked'] = currQuestion.isScreenMarked
      }

      return q
    })
    setQuestions(updatedQuestions)
    setShowCalculator(false)
  }

  const nextQuestion = () => {
    saveExpressionAndMarkings()
    if (questionIdx < questions.length) {
      setQuestionIdx(questionIdx + 1)
    }
  }

  const previousQuestion = () => {
    saveExpressionAndMarkings()
    if (questionIdx > 0) {
      setQuestionIdx(questionIdx - 1)
    }
  }

  const onChangeQuestionFromTopBar = (questionIndex: number) => {
    saveExpressionAndMarkings()
    setQuestionIdx(questionIndex)
  }

  const onRevert = async () => {
    if ((isFormative || isBenchmark) && id && classroom_id) {
      asyncFetchQuestions(assessmentType, id, classroom_id)
    }
  }

  const onSave = async () => {
    const updatedQuestions = questions.filter((question) => question.updated)

    if ((isFormative || isBenchmark) && updatedQuestions.length) {
      await updateQuestions(updatedQuestions, assessmentType)
      setQuestions(questions.map((q) => ({ ...q, updated: false })))
    }
  }

  const onClear = async () => {
    const oldAnswer = questions[questionIdx].answer

    questions[questionIdx] = {
      ...questions[questionIdx],
      updated: false,
      answer: null!
    }

    setQuestions([...questions])
    socketRef.current?.emit(SOCKET_STUDENT_UPDATE_ANSWER, {
      classroom_id,
      test_type: getSingularAssessmentType(assessmentType),
      answer: null,
      bookmark: false,
      test_id: id,
      question_id: currentQuestion.id,
      student_id
    })

    if ((isFormative || isBenchmark) && id && classroom_id) {
      await submitAnswer(
        assessmentType,
        parseInt(id),
        currentQuestion.id,
        oldAnswer,
        parseInt(classroom_id),
        0,
        true
      )
    }
  }

  const onExit = async () => {
    socketRef.current?.emit(SOCKET_STUDENT_LEAVE_ROOM, {
      student_id: student_id,
      test_type: getSingularAssessmentType(assessmentType),
      test_id: id,
      classroom_id: classroom_id
    })
  }

  const onAnswerChange = async (choice: number) => {
    /**
     * Read only benchmark review must never submit or mutate responses even if
     * the rendered choice UI is clicked.
     */
    if (isReadOnlyViewMode) return

    onChange('answer', choice)
    socketRef.current?.emit(SOCKET_STUDENT_UPDATE_ANSWER, {
      classroom_id,
      answer: choice,
      test_type: getSingularAssessmentType(assessmentType),
      test_id: id,
      question_id: currentQuestion.id,
      student_id
    })

    if ((isFormative || isBenchmark) && id && classroom_id) {
      const answerPromise = submitAnswer(
        assessmentType,
        parseInt(id),
        currentQuestion.id,
        choice,
        parseInt(classroom_id),
        0
      )
      setAnswerPromises((prev) => [...prev, answerPromise])
    }
  }

  const onChange = async (key: string, value: any) => {
    questions[questionIdx] = {
      ...questions[questionIdx],
      [key]: value,
      updated: true
    }

    setQuestions([...questions])
    if (
      key === 'bookmark' &&
      (isFormative || isBenchmark) &&
      id &&
      classroom_id
    ) {
      await toggleBookmark(
        assessmentType,
        parseInt(id),
        parseInt(classroom_id),
        value,
        parseInt(currentQuestion.id)
      )
    }
  }

  const setIsScreenMarked = (value: boolean) => {
    if (value && questions[questionIdx].isScreenMarked) return

    questions[questionIdx] = {
      ...questions[questionIdx],
      isScreenMarked: value
    }

    setQuestions([...questions])
  }

  const hasNextQuestion = questionIdx < questions.length - 1
  const hasPreviousQuestion = questionIdx > 0

  //called when questions changes
  useEffect(() => {
    const element = document.getElementById(keyToSpeak)
    element?.classList.remove('playtts')
    setTabIndex(0)
    currentQuestion &&
      socketInit &&
      socketRef.current?.emit(SOCKET_STUDENT_UPDATE_POSITION_QUESTION, {
        classroom_id,
        test_type: getSingularAssessmentType(assessmentType),
        test_id: id,
        question_id: currentQuestion.id,
        student_id
      })

    if (audioRef.current?.paused === false) {
      audioRef.current?.pause()
      setKeyToSpeak(null!)
    }
  }, [questionIdx])

  useEffect(() => {
    currentQuestion?.id &&
      socketInit &&
      !initUpdatePostion &&
      socketRef?.current.emit(SOCKET_STUDENT_UPDATE_POSITION_QUESTION, {
        classroom_id,
        test_id: id,
        test_type: getSingularAssessmentType(assessmentType),
        question_id: currentQuestion.id,
        student_id
      })

    currentQuestion.id && socketInit && setInitUpdatePostion(true)
  }, [questions, socketInit])

  const getCalculatorElement = () => {
    const elt = document.getElementById('desmos-calculator')
    return elt
  }

  const destroyCalculator = () => {
    const elt = getCalculatorElement()
    if (!elt) return
    const calculator = getCalculator()
    calculator && calculator.destroy()
    setCalculator(null)
    elt.style.display = 'none'
  }

  const closeCalculator = () => {
    setShowCalculator(false)
    destroyCalculator()
  }

  const isTestUpdated = questions.some((q) => q.updated)

  const hasPassage = currentQuestion.passage_1 !== null
  const showTabs = currentQuestion.passage_2 !== null

  return (
    <QuestionnaireContext.Provider
      value={{
        audioRef,
        destroyCalculator,
        getCalculatorElement,
        closeCalculator,
        isPausedByTeacher,
        setIsPausedByTeacher,
        setIsScreenMarked,
        onSubmitAssessment,
        setIsPaused,
        isPaused,
        setIsPencilActive,
        isPencilActive,
        onSpeak,
        keyToSpeak,
        setKeyToSpeak,
        keys,
        isSpeechIconClicked,
        setIsSpeechIconClicked,
        setIsHighlighterActive,
        isHighlighterActive,
        setShowCalculator,
        showCalculator,
        showFinishModal,
        setShowFinishModal,
        isSocketReady,
        hasPassage,
        showTabs,
        show,
        setShow,
        questionIdx,
        setQuestionIdx,
        currentQuestion,
        questions,
        nextQuestion,
        previousQuestion,
        hasNextQuestion,
        hasPreviousQuestion,
        isTestUpdated,
        onRevert,
        onSave,
        onChange,
        setSearchParams,
        viewMode,
        tabIndex,
        setTabIndex,
        showSaveModal,
        setShowSaveModal,
        onAnswerChange,
        onClear,
        onExit,
        showClearModal,
        setShowClearModal,
        showPauseExitModal,
        setShowPauseExitModal,
        onChangeQuestionFromTopBar,
        showUnAvailableTestModal,
        setShowUnAvailableTestModal,
        setAssessmentScore,
        assessmentScore,
        socketRef,
        saveExpressionAndMarkings,
        containerRef,
        submitTestLoading,
        grade,
        setShowHighlightEraser,
        showHighlightEraser,
        setSubmitTestLoading,
        setIsExpand,
        isExpand,
        answerPromises,
        setAnswerPromises,
        setAnswerSubmittingModal,
        answerSubmittingModal,
        sketchWidth,
        setSketchWidth,
        sketchLeft,
        setSketchLeft,
        scrolledTop,
        setScrolledTop,
        setSketchHeight,
        sketchHeight,
        setSketchHeightQues,
        setSketchWidthQues,
        sketchHeightQues,
        sketchWidthQues
      }}
    >
      {props.children}
    </QuestionnaireContext.Provider>
  )
}

export default QuestionnaireContextProvider
